import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, 
  Rocket,
  Plus,
  X,
  Calendar,
  CheckCircle2,
  FileText,
  RefreshCw,
  Sparkles
} from "lucide-react";
import StepWrapper from "./StepWrapper";
import { toast } from "sonner";

const StepNextSteps = ({ data, updateData, onBack, onViewSummary, onStartOver, coachMode = true }) => {
  const [newStep, setNewStep] = useState("");
  
  // Check if user has made progress (commitment or next steps)
  const isReadyToCommit = data.commitment.trim() !== "" || data.nextSteps.length > 0;

  const addNextStep = () => {
    if (newStep.trim()) {
      updateData("nextSteps", [...data.nextSteps, { text: newStep.trim(), completed: false }]);
      setNewStep("");
    }
  };

  const removeNextStep = (index) => {
    updateData("nextSteps", data.nextSteps.filter((_, i) => i !== index));
  };

  const toggleStepComplete = (index) => {
    const updated = data.nextSteps.map((step, i) => 
      i === index ? { ...step, completed: !step.completed } : step
    );
    updateData("nextSteps", updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addNextStep();
    }
  };

  return (
    <StepWrapper
      stepNumber={6}
      title="Commit & Take Action"
      subtitle={coachMode ? "Define your commitment and identify the specific next steps you'll take." : null}
    >
      <div className="space-y-6">
        {/* Commitment Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Your Commitment</h2>
                {coachMode && <p className="text-sm text-muted-foreground">State what you&apos;re going to do</p>}
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="commitment" className="text-sm font-medium text-foreground mb-2 block">
                  I am going to:
                </Label>
                <Textarea
                  id="commitment"
                  value={data.commitment}
                  onChange={(e) => updateData("commitment", e.target.value)}
                  placeholder="Write your commitment statement... e.g., 'I will apply to three colleges that match my values by the end of this month.'"
                  className="min-h-[120px] resize-none bg-background border-input text-base"
                />
              </div>
              
              {/* Target Date */}
              <div>
                <Label htmlFor="targetDate" className="text-sm font-medium text-foreground mb-2 block">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Target Date
                </Label>
                <Input
                  id="targetDate"
                  type="date"
                  value={data.targetDate}
                  onChange={(e) => updateData("targetDate", e.target.value)}
                  className="bg-background border-input max-w-xs"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-chain-commitment/20 flex items-center justify-center">
                <Rocket className="w-5 h-5 text-chain-commitment" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Specific Next Steps</h2>
                <p className="text-sm text-muted-foreground">Break down your commitment into actionable steps</p>
              </div>
            </div>

            {/* Add new step */}
            <div className="flex gap-2 mb-4">
              <Input
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add a next step..."
                className="flex-1 bg-background border-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="icon"
                onClick={addNextStep}
                disabled={!newStep.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Steps list */}
            {data.nextSteps.length > 0 ? (
              <ul className="space-y-2">
                {data.nextSteps.map((step, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 bg-muted rounded-lg group"
                  >
                    <Checkbox
                      checked={step.completed}
                      onCheckedChange={() => toggleStepComplete(index)}
                      className="shrink-0"
                    />
                    <span className={`flex-1 text-sm ${step.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {step.text}
                    </span>
                    <button
                      onClick={() => removeNextStep(index)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No steps added yet. Break your commitment into small, actionable steps.</p>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Completion Summary */}
        {isReadyToCommit && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6 bg-success/5 border-2 border-success/30">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-success" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Great Work!
                </h3>
                <p className="text-muted-foreground mb-6">
                  You've worked through your decision with clarity. You have a commitment and actionable next steps.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={onViewSummary}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Summary
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onStartOver}
                    className="border-muted-foreground/30"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Start New Decision
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onViewSummary}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            <FileText className="w-4 h-4 mr-2" />
            View Summary
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepNextSteps;
